import api from './api';
import type {
  Email,
  EmailThread,
  EmailFolder,
  EmailFilter,
  EmailComposer,
  PaginationParams,
  EmailListResponse,
} from '@/types';

/**
 * Resultado de un reenvío. `skippedAttachments` trae los adjuntos del original
 * que el proveedor no pudo copiar, para poder avisarlo en pantalla.
 * `errorMessage` es el mensaje concreto del backend (404/413/502 de un correo
 * adjunto, por ejemplo) cuando `ok` es `false`.
 */
export interface ForwardResult {
  ok: boolean;
  skippedAttachments: string[];
  errorMessage?: string;
}

/**
 * Resultado de eliminar un correo. `associated`: el backend lo rechazó (409
 * EMAIL_HAS_ASSOCIATION) porque tiene un embarque u orden de compra asociada.
 */
export type EmailDeleteOutcome = 'deleted' | 'associated' | 'failed';

function deleteOutcome(response: { success: boolean; error?: { details?: any } }): EmailDeleteOutcome {
  if (response.success) return 'deleted';
  return response.error?.details?.code === 'EMAIL_HAS_ASSOCIATION' ? 'associated' : 'failed';
}

/** Resultado de enviar o responder un correo. */
export interface SendResult {
  email: Email | null;
  errorMessage?: string;
}

/** Resultado de marcar un lote de correos como leídos/no leídos. */
export interface BulkReadResult {
  updated: number;
  succeeded: string[];
  failed: Array<{ id: string; reason: string }>;
}

/** Tope de ids por petición que acepta PATCH /emails/read en el backend. */
const BULK_READ_CHUNK_SIZE = 500;

export interface EmailDraftInput {
  mode?: 'new' | 'reply' | 'replyAll' | 'forward';
  inReplyTo?: string | null;
  to?: EmailComposer['to'];
  cc?: EmailComposer['cc'];
  bcc?: EmailComposer['bcc'];
  subject?: string;
  body?: string;
  bodyType?: 'html' | 'text';
  importance?: 'low' | 'normal' | 'high';
  contextModule?: string | null;
  contextEntityId?: string | null;
  contextEntityIdNumber?: string | null;
  /** Solo metadatos: los archivos se vuelven a adjuntar al enviar */
  attachments?: Array<{ name: string; size: number; contentType: string }>;
  /** Correos de Kila adjuntados por referencia: basta el id para volver a adjuntarlos al enviar */
  attachedEmails?: Array<{ emailId: string; subject: string }>;
}

export interface EmailDraft extends Required<Pick<EmailDraftInput, 'mode' | 'to' | 'cc' | 'bcc' | 'subject' | 'body' | 'bodyType' | 'attachments'>> {
  id: string;
  userId: string;
  inReplyTo?: string | null;
  importance?: 'low' | 'normal' | 'high';
  contextModule?: string | null;
  contextEntityId?: string | null;
  contextEntityIdNumber?: string | null;
  /** Correos de Kila adjuntados por referencia: basta el id para volver a adjuntarlos al enviar */
  attachedEmails?: Array<{ emailId: string; subject: string }>;
  createdAt?: { _seconds?: number; seconds?: number } | string;
  updatedAt?: { _seconds?: number; seconds?: number } | string;
}

class EmailService {
  async getEmails(
    params: PaginationParams & EmailFilter & { search?: string }
  ): Promise<EmailListResponse | null> {
    const response = await api.get<EmailListResponse>('/emails', params);
    return response.success && response.data ? response.data : null;
  }

  async getEmailById(id: string): Promise<Email | null> {
    const response = await api.get<Email>(`/emails/${id}`);
    return response.success && response.data ? response.data : null;
  }

  async getEmailThread(conversationId: string): Promise<EmailThread | null> {
    const response = await api.get<EmailThread>(`/emails/thread/${conversationId}`);
    return response.success && response.data ? response.data : null;
  }

  async sendEmail(emailData: EmailComposer): Promise<SendResult> {
    const formData = new FormData();
    
    
    // Agregar campos de texto
    formData.append('to', JSON.stringify(emailData.to));
    if (emailData.cc && emailData.cc.length > 0) {
      formData.append('cc', JSON.stringify(emailData.cc));
    }
    if (emailData.bcc && emailData.bcc.length > 0) {
      formData.append('bcc', JSON.stringify(emailData.bcc));
    }
    formData.append('subject', emailData.subject);
    formData.append('body', emailData.body);
    formData.append('bodyType', emailData.bodyType || 'html');
    // Respuesta a un correo embebido (.eml) sin documento en Firestore: Gmail
    // los usa para enganchar el hilo, Microsoft los ignora sin error.
    if (emailData.inReplyTo) formData.append('inReplyTo', emailData.inReplyTo);
    if (emailData.references) formData.append('references', emailData.references);
    if (emailData.importance) {
      formData.append('importance', emailData.importance);
    }
    if (emailData.contextModule) {
      formData.append('contextModule', emailData.contextModule);
    }
    if (emailData.contextEntityId) {
      formData.append('contextEntityId', emailData.contextEntityId);
    }
    if (emailData.contextEntityIdNumber) {
      formData.append('contextEntityIdNumber', emailData.contextEntityIdNumber);
    }

    // Correos de Kila adjuntados por referencia: el backend baja su MIME.
    if (emailData.attachedEmailIds && emailData.attachedEmailIds.length > 0) {
      formData.append('attachedEmailIds', JSON.stringify(emailData.attachedEmailIds));
    }

    // Personas mencionadas con @: el backend les deja el aviso en la campana.
    if (emailData.mentions && emailData.mentions.length > 0) {
      formData.append('mentions', JSON.stringify(emailData.mentions));
    }

    // Agregar archivos adjuntos si existen
    if (emailData.attachments && emailData.attachments.length > 0) {
      emailData.attachments.forEach((file, index) => {
        formData.append('attachments', file);
      });
    } else {
    }

    const response = await api.post<Email>('/emails/send', formData);
    return {
      email: response.success && response.data ? response.data : null,
      errorMessage: response.success ? undefined : response.error?.message,
    };
  }

  async replyToEmail(emailId: string, replyData: Partial<EmailComposer>, replyAll: boolean = false): Promise<SendResult> {
    const formData = new FormData();

    formData.append('body', replyData.body || '');
    formData.append('bodyType', replyData.bodyType || 'html');
    formData.append('replyAll', String(replyAll));
    // El usuario puede editar el asunto al responder. Si no viaja, el backend
    // lo deriva del original ("RE: ...") y el cambio se pierde.
    if (replyData.subject) {
      formData.append('subject', replyData.subject);
    }

    // Lo mismo con los destinatarios: sin ellos el proveedor responde a los del
    // correo original e ignora los que puso la plantilla. cc y bcc viajan aunque
    // estén vacíos, para poder borrar los que trae el hilo.
    if (replyData.to?.length) {
      formData.append('to', JSON.stringify(replyData.to));
      formData.append('cc', JSON.stringify(replyData.cc || []));
      formData.append('bcc', JSON.stringify(replyData.bcc || []));
    }

    // Contexto del módulo desde el que se escribió: el backend deja la respuesta
    // asociada al embarque/OC al momento de enviarla.
    if (replyData.contextModule) {
      formData.append('contextModule', replyData.contextModule);
    }
    if (replyData.contextEntityId) {
      formData.append('contextEntityId', replyData.contextEntityId);
    }
    if (replyData.contextEntityIdNumber) {
      formData.append('contextEntityIdNumber', replyData.contextEntityIdNumber);
    }

    // Correos de Kila adjuntados por referencia: el backend baja su MIME.
    if (replyData.attachedEmailIds && replyData.attachedEmailIds.length > 0) {
      formData.append('attachedEmailIds', JSON.stringify(replyData.attachedEmailIds));
    }

    // Personas mencionadas con @: el backend les deja el aviso en la campana.
    if (replyData.mentions && replyData.mentions.length > 0) {
      formData.append('mentions', JSON.stringify(replyData.mentions));
    }

    if (replyData.attachments && replyData.attachments.length > 0) {
      replyData.attachments.forEach((file, index) => {
        formData.append('attachments', file);
      });
    }

    const response = await api.post<Email>(`/emails/${emailId}/reply`, formData);
    return {
      email: response.success && response.data ? response.data : null,
      errorMessage: response.success ? undefined : response.error?.message,
    };
  }

  /**
   * Reenvía apoyándose en el endpoint nativo del proveedor: es el único camino
   * que conserva los adjuntos del correo original. Enviarlo como correo nuevo
   * por /emails/send los perdía siempre.
   *
   * El cuerpo viaja como `comment` (el nombre que espera el backend) e incluye
   * la cita, porque el borrador de Graph reemplaza el cuerpo completo.
   */
  async forwardEmail(emailId: string, forwardData: Partial<EmailComposer>): Promise<ForwardResult> {
    const formData = new FormData();

    formData.append('to', JSON.stringify(forwardData.to || []));
    if (forwardData.cc && forwardData.cc.length > 0) {
      formData.append('cc', JSON.stringify(forwardData.cc));
    }
    if (forwardData.bcc && forwardData.bcc.length > 0) {
      formData.append('bcc', JSON.stringify(forwardData.bcc));
    }
    formData.append('comment', forwardData.body || '');
    formData.append('bodyType', forwardData.bodyType || 'html');
    if (forwardData.subject) {
      formData.append('subject', forwardData.subject);
    }

    // Los adjuntos del original que el usuario quitó en el compositor. El
    // backend los descarta: el id sirve donde se listan los del original, y el
    // nombre con el tamaño en el borrador de Graph, que les asigna ids nuevos.
    // Si no hay ninguno el campo no viaja, y el backend se comporta como antes.
    if (forwardData.excludeAttachments && forwardData.excludeAttachments.length > 0) {
      formData.append('excludeAttachments', JSON.stringify(forwardData.excludeAttachments));
    }

    // Correos de Kila adjuntados por referencia: el backend baja su MIME.
    if (forwardData.attachedEmailIds && forwardData.attachedEmailIds.length > 0) {
      formData.append('attachedEmailIds', JSON.stringify(forwardData.attachedEmailIds));
    }

    // Personas mencionadas con @: el backend les deja el aviso en la campana.
    if (forwardData.mentions && forwardData.mentions.length > 0) {
      formData.append('mentions', JSON.stringify(forwardData.mentions));
    }

    // Solo los archivos que agregó el usuario: los del original los adjunta el
    // proveedor y nunca pasan por el navegador.
    if (forwardData.attachments && forwardData.attachments.length > 0) {
      forwardData.attachments.forEach(file => {
        formData.append('attachments', file);
      });
    }

    const response = await api.post<{ sent: boolean; skippedAttachments?: string[] }>(
      `/emails/${emailId}/forward`,
      formData
    );

    return {
      ok: response.success && response.data?.sent !== false,
      skippedAttachments: response.data?.skippedAttachments || [],
      errorMessage: response.success ? undefined : response.error?.message,
    };
  }

  async markAsRead(emailId: string, isRead: boolean = true, companyId?: string): Promise<boolean> {
    const cId = companyId || localStorage.getItem('emailCompanyId') || '';
    const response = await api.patch(`/emails/${emailId}/read`, { isRead, companyId: cId });
    return response.success;
  }

  /**
   * Marca varios correos en UNA petición (PATCH /emails/read).
   *
   * Antes la bandeja mandaba un PATCH por correo: al marcar cientos de no
   * leídos la ráfaga agotaba el limitador del backend y durante el resto del
   * minuto también fallaba la carga del propio buzón, que es lo que el usuario
   * veía como "no se actualiza". El backend resuelve el lote con batchModify
   * (Gmail) o $batch (Graph).
   *
   * Se trocea porque el backend acepta como mucho 1.000 ids por petición, y los
   * trozos van en serie para no reconstruir la ráfaga que se quiere evitar.
   */
  async markManyAsRead(
    emailIds: string[],
    isRead: boolean = true,
    companyId?: string
  ): Promise<BulkReadResult> {
    const cId = companyId || localStorage.getItem('emailCompanyId') || '';
    const merged: BulkReadResult = { updated: 0, succeeded: [], failed: [] };

    for (let i = 0; i < emailIds.length; i += BULK_READ_CHUNK_SIZE) {
      const chunk = emailIds.slice(i, i + BULK_READ_CHUNK_SIZE);
      const response = await api.patch(`/emails/read`, { ids: chunk, isRead, companyId: cId });

      if (!response.success) {
        merged.failed.push(...chunk.map(id => ({ id, reason: 'La petición no llegó al servidor' })));
        continue;
      }

      // api.patch no desenvuelve el sobre { success, message, data } como sí
      // hace api.get, así que se admiten las dos formas.
      const payload = ((response.data as any)?.data ?? response.data ?? {}) as Partial<BulkReadResult>;
      merged.updated += payload.updated ?? 0;
      merged.succeeded.push(...(payload.succeeded ?? []));
      merged.failed.push(...(payload.failed ?? []));
    }

    return merged;
  }

  async markThreadAsRead(threadId: string, isRead: boolean = true, companyId?: string): Promise<boolean> {
    const cId = companyId || localStorage.getItem('emailCompanyId') || '';
    const response = await api.patch(
      `/emails/thread/${encodeURIComponent(threadId)}/read`,
      { isRead, companyId: cId }
    );
    return response.success;
  }

  async toggleFlag(emailId: string, isFlagged: boolean = true, companyId?: string): Promise<boolean> {
    const cId = companyId || localStorage.getItem('emailCompanyId') || '';
    const response = await api.patch(`/emails/${emailId}/flag`, { isFlagged, companyId: cId });
    return response.success;
  }

  async deleteEmail(emailId: string, companyId?: string): Promise<EmailDeleteOutcome> {
    const cId = companyId || localStorage.getItem('emailCompanyId') || '';
    const response = await api.delete(`/emails/${emailId}`, { data: { companyId: cId } });
    return deleteOutcome(response);
  }

  async permanentDeleteEmail(emailId: string, companyId?: string): Promise<EmailDeleteOutcome> {
    const cId = companyId || localStorage.getItem('emailCompanyId') || '';
    const response = await api.delete(`/emails/${emailId}/permanent`, { data: { companyId: cId } });
    return deleteOutcome(response);
  }

  /**
   * Recupera un correo de la papelera. A diferencia de mover a la bandeja, esto
   * también restaura la copia de Kila (asociación al embarque, análisis de IA)
   * aunque el proveedor ya no tenga el mensaje.
   */
  async restoreEmail(emailId: string, destinationFolder = 'inbox', companyId?: string): Promise<boolean> {
    const cId = companyId || localStorage.getItem('emailCompanyId') || '';
    const response = await api.post(`/emails/${emailId}/restore`, { destinationFolder, companyId: cId });
    return response.success;
  }

  /**
   * Mueve un correo. `destinationFolder` es la clave canónica ('archive',
   * 'inbox', 'trash') o el id de una carpeta del usuario; en ese caso
   * `destinationFolderName` le ahorra al backend una consulta al proveedor
   * para guardar el nombre con el que Kila agrupa la carpeta.
   */
  async moveEmail(
    emailId: string,
    destinationFolder: string,
    companyId?: string,
    destinationFolderName?: string,
    sourceFolder?: string
  ): Promise<boolean> {
    const cId = companyId || localStorage.getItem('emailCompanyId') || '';
    const response = await api.patch(`/emails/${emailId}/move`, {
      destinationFolder,
      ...(destinationFolderName ? { destinationFolderName } : {}),
      // En Gmail una carpeta es una etiqueta: el backend quita la de origen
      // para que el correo no quede en los dos lados, como hace "Mover a".
      ...(sourceFolder ? { sourceFolder } : {}),
      companyId: cId,
    });
    return response.success;
  }

  async getFolders(): Promise<EmailFolder[]> {
    const response = await api.get<EmailFolder[]>('/emails/folders');
    return response.success && response.data ? response.data : [];
  }

  /** Crea una carpeta (Outlook) o etiqueta (Gmail) en el buzón del usuario. */
  async createFolder(displayName: string, parentFolderId?: string): Promise<EmailFolder | null> {
    const response = await api.post<EmailFolder>('/emails/folders', { displayName, parentFolderId });
    return response.success && response.data ? response.data : null;
  }

  async renameFolder(folderId: string, displayName: string): Promise<EmailFolder | null> {
    const response = await api.patch<EmailFolder>(`/emails/folders/${folderId}`, { displayName });
    return response.success && response.data ? response.data : null;
  }

  async deleteFolder(folderId: string): Promise<boolean> {
    const response = await api.delete(`/emails/folders/${folderId}`);
    return response.success;
  }

  async getMailboxStorage(): Promise<{ used: number; total: number; percentage: number }> {
    const response = await api.get<{ used: number; total: number; percentage: number }>('/emails/storage');
    return response.data || { used: 0, total: 50, percentage: 0 };
  }

  async searchEmails(query: string, params?: PaginationParams): Promise<EmailListResponse | null> {
    const response = await api.get<EmailListResponse>('/emails/search', {
      q: query,
      ...params,
    });
    return response.success && response.data ? response.data : null;
  }

  // ── Borradores guardados en Kila (Companies/{companyId}/emailDrafts) ──
  // No dependen del proveedor: el usuario redacta, cierra y retoma después.

  async getDrafts(): Promise<EmailDraft[]> {
    const response = await api.get<EmailDraft[]>('/emails/drafts');
    return response.success && response.data ? response.data : [];
  }

  async getDraft(draftId: string): Promise<EmailDraft | null> {
    const response = await api.get<EmailDraft>(`/emails/drafts/${draftId}`);
    return response.success && response.data ? response.data : null;
  }

  async saveDraft(draftData: EmailDraftInput): Promise<EmailDraft | null> {
    const response = await api.post<EmailDraft>('/emails/drafts', draftData);
    return response.success && response.data ? response.data : null;
  }

  async updateDraft(draftId: string, draftData: EmailDraftInput): Promise<EmailDraft | null> {
    const response = await api.put<EmailDraft>(`/emails/drafts/${draftId}`, draftData);
    return response.success && response.data ? response.data : null;
  }

  async deleteDraft(draftId: string): Promise<boolean> {
    const response = await api.delete(`/emails/drafts/${draftId}`);
    return response.success;
  }

  async enrichAttachmentContentIds(emailId: string): Promise<boolean> {
    const response = await api.patch<{ enriched: boolean }>(`/emails/${emailId}/enrich-attachments`);
    return response.success && !!response.data?.enriched;
  }
}

export default new EmailService();
